import type { ReactNode } from 'react'
import { barGeometry } from '../shared/bar'
import { kickoffWithDate, windowLabel } from '../shared/clock'
import type { Side } from '../shared/espn'
import type { StateGame } from './api'

/*
 * The board's primitives, built from reference/design-handoff/README.md.
 *
 * Nothing in this file names a colour. Every value comes from a token in
 * index.css, which is what let the whole visual direction change - white, true
 * black and a blue became a light ground, navy ink, gold and mint - without a
 * single component needing to know.
 *
 * The three jobs survived that change even though every value in them did not:
 * gold is something YOU did, slate is a fact about the GAME, mint is TIME.
 */

/**
 * A team name never wraps and never truncates.
 *
 * 22px, stepping to 19px at thirteen characters. Measured across all 760 teams
 * ESPN lists, the longest short name is 15 characters ("Holland College") and
 * the realistic worst case here is "Boston College". Half a 390px card cannot
 * hold that at 22px, so the step is a rule - it fires on roughly one game in
 * twelve, which is far too often to leave to chance.
 */
function nameSize(name: string): string {
  return name.length >= 13 ? 'text-[19px]' : 'text-[22px]'
}

export function SpreadTrack({ game: g }: { game: StateGame }) {
  const { side, pct } = barGeometry(g.spread, g.favSide)
  const favName = g.favSide === 'home' ? g.home.name : g.away.name

  return (
    <div className="relative mt-[9px] h-[14px]">
      {/* The rule the whole track hangs on. */}
      <div className="absolute top-[6px] right-0 left-0 h-[2px] rounded-[1px] bg-hairline" />

      {g.spread === null ? (
        /*
         * The common state, not an edge case: 55 of 58 games in a mid-season
         * week had no line posted. A white plate interrupting the rule says so
         * quietly - and is deliberately not shaped like an input, which is
         * what the old dashed box read as.
         */
        <span className="slate-num absolute top-[1px] left-1/2 -translate-x-1/2 bg-card px-2 text-[9px] font-medium tracking-[0.08em] whitespace-nowrap text-pale">
          LINE NOT POSTED
        </span>
      ) : (
        <>
          <div className="absolute top-[2px] left-1/2 h-[10px] w-[2px] -translate-x-px bg-slate" />
          {side && (
            <div
              className="absolute top-[5px] h-[4px] bg-slate"
              // radius only on the outer end, so it grows away from the hash
              style={
                side === 'home'
                  ? { left: '50%', width: `${pct}%`, borderRadius: '0 2px 2px 0' }
                  : { right: '50%', width: `${pct}%`, borderRadius: '2px 0 0 2px' }
              }
            />
          )}
          {side && (
            <span
              className="slate-num absolute top-[1px] bg-card px-[5px] text-[9px] font-medium tracking-[0.06em] whitespace-nowrap text-mute"
              /*
               * Pinned to the END OF THE TRACK on the favourite's side, not to
               * the end of the fill. Following the fill, a −13.5 line pushed
               * the label 17px past the track, across the card's border and to
               * within 8px of the screen edge; a short line left it hovering
               * beside the centre hash as though it labelled the hash.
               */
              style={side === 'home' ? { right: 0 } : { left: 0 }}
            >
              {favName.toUpperCase()} &minus;{g.spread}
            </span>
          )}
        </>
      )}
    </div>
  )
}

export interface RowProps {
  game: StateGame
  picked: Side | null
  onPick: (side: Side) => void
  tz: string
  /**
   * The player locked their own board with "Lock it in". Shuts the cells like
   * the deadline does, until they reopen it - a lock you can tap straight
   * through is not a lock.
   */
  held?: boolean
}

/**
 * One game.
 *
 * The pick ladder is rings and tints rather than strengths of a single fill,
 * which is what lets the team name stay ink at every step except the last.
 * Only the won state goes solid, and only there does the type flip.
 *
 *   unpicked      white, hairline border - the ONLY bordered state
 *   picked-open   inset 1.5px gold ring + 13% gold tint
 *   locked        inset 2px darker-gold ring + 20% tint   (board deadline passed)
 *   won           solid gold, night ink, CORRECT tag
 *   lost          recessed fill, struck name, faded ring
 */
/**
 * A team's crest, or nothing at all.
 *
 * Hotlinked from a.espncdn.com, which we do not control and which has no
 * obligation to keep serving us - so a failure REMOVES the element rather than
 * leaving the browser's broken-image glyph. If ESPN ever blocks it, every
 * screen quietly becomes the typographic one it is today.
 *
 * `alt=""` on purpose: the team name is always beside it in text, and a
 * screen reader announcing "Utah Utes logo, Utah" is worse than silence.
 */
export function TeamLogo({ src, size = 28 }: { src: string | null; size?: number }) {
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      style={{ width: size, height: size, objectFit: 'contain' }}
      onError={(e) => {
        e.currentTarget.style.display = 'none'
      }}
    />
  )
}

export function GameCard({ game: g, picked, onPick, tz, held = false }: RowProps) {
  const done = g.result?.final === true
  /*
   * TWO FACTS, and they stopped being one the day the board grew a single
   * deadline. `locked` is whether this pick can still be changed - the
   * board's own deadline, true for all ten games from the first kickoff.
   * `started` is whether THIS game has kicked off, and it is the only thing
   * that can put a score on the card.
   *
   * Everything about the AFFORDANCE hangs off locked: a cell that invites a
   * tap the server will refuse is worse than one that is plainly shut.
   *
   * `held` shuts it too, from the player's own side. It is kept off
   * StateGame on purpose: `locked` there also means "the picks are public",
   * and a player locking early makes nobody's picks public.
   */
  const locked = g.locked || held
  const started = g.started
  const won = (s: Side) => done && g.result?.winnerSide === s
  // The deadline only. A held board can still be reopened and filled in.
  const noPick = g.locked && picked === null

  const cell = (s: Side) => {
    const team = s === 'home' ? g.home : g.away
    const on = picked === s
    const score = s === 'home' ? g.result?.homeScore : g.result?.awayScore

    /*
     * A BAR ON THE INNER EDGE, not a ring.
     *
     * The written spec said "inset 1.5px gold ring", which is four sides. The
     * approved screenshots show one: a 1.5px vertical bar at x=190 on a cell
     * spanning 25-190, i.e. the edge facing the opponent. Measured #E9B44C
     * when the pick is still open and #C89235 once the game has kicked off.
     *
     * Both examples in the screenshots are the LEFT cell, so the bar could be
     * read as "always the right edge". Inner edge is the reading taken here:
     * it points at the matchup divide, and it keeps the card balanced when the
     * right-hand team is the one picked, where a right-edge bar would sit out
     * against the card padding. If that is wrong it is one ternary.
     */
    const inner = s === 'away' ? '-1.5px' : '1.5px'
    const bar = (colour: string) => `inset ${inner} 0 0 0 ${colour}`

    let box = 'border border-hairline bg-card'  // the only bordered state
    let name = 'text-ink'
    let ring: string | undefined
    if (on && !locked) {
      box = 'border border-transparent bg-gold/13'
      ring = bar('var(--color-gold)')
    } else if (on && !done) {
      box = 'border border-transparent bg-gold/20'
      ring = bar('var(--color-gold-edge)')
    } else if (on && won(s)) {
      box = 'border border-transparent bg-gold'
      name = 'text-nightink'
    } else if (on) {
      box = 'border border-transparent bg-ground'
      name = 'text-faint line-through'
      ring = bar('rgb(233 180 76 / 0.4)')
    }

    return (
      <button
        type="button"
        disabled={locked}
        onClick={() => onPick(s)}
        aria-pressed={on}
        /* 74px measured off the approved screenshot, not the 60px the written
           spec claims - a name-only cell and a name-plus-score cell are both
           74 tall, so the height is fixed and the content centres. */
        className={[
          'relative flex min-h-[74px] flex-1 flex-col items-center justify-center gap-0.5 rounded-[4px] px-[10px] py-[6px] transition-colors',
          box,
          locked ? 'cursor-default' : 'active:opacity-90',
        ].join(' ')}
        style={ring ? { boxShadow: ring } : undefined}
      >
        {/*
         * The rank is pinned to the corner, never inline with the name and
         * never near a score. "21 UTAH" set inline reads as Utah having 21
         * points, which in a football app is the one reading you cannot allow.
         */}
        {team.rank !== null && (
          <span
            className={[
              'slate-num absolute top-[6px] left-[8px] text-[11px] font-semibold',
              /* faint over a gold tint measured 2.4:1 - unreadable. */
              on ? 'text-gold-text' : 'text-mute',
            ].join(' ')}
          >
            {team.rank}
          </span>
        )}

        {/*
         * Above the name, not beside it. The card is a vertical stack and the
         * name is the widest thing in it; a crest on the same line would push
         * a long name into the truncation this card is built to avoid.
         *
         * It is here rather than on the grid or the standings because this is
         * the screen where speed of recognition matters - an eight-year-old
         * finds a crest faster than he reads TTU - and the only one with room.
         */}
        <TeamLogo src={team.logo} size={30} />

        <span
          className={[
            'mt-1 font-bold tracking-[0.02em] whitespace-nowrap uppercase',
            nameSize(team.name),
            name,
          ].join(' ')}
        >
          {team.name}
        </span>

        {started && score !== null && score !== undefined && (
          <span
            className={[
              'slate-num text-[30px] font-bold',
              won(s) && on ? 'text-nightink' : 'text-slate',
            ].join(' ')}
          >
            {score}
          </span>
        )}

        {on && won(s) && (
          <span className="slate-num text-[8.5px] font-medium tracking-[0.12em] text-nightink/70">
            &#10003; CORRECT
          </span>
        )}
      </button>
    )
  }

  return (
    <li className="slate-card px-3 pt-[9px] pb-3">
      <div className="slate-num mb-2 flex justify-between text-[9.5px] font-medium tracking-[0.1em]">
        {/*
         * Mint is time and only time. A game being on television is not an
         * error, which is why there is no red anywhere in this palette.
         */}
        {done ? (
          <span className="text-mute">FINAL</span>
        ) : started ? (
          <span className="font-bold text-live-deep">&#9679; LIVE</span>
        ) : (
          <span className="text-mute">{kickoffWithDate(g.kickoffAt, tz)}</span>
        )}
        <span className="text-mute">
          {noPick ? 'NO PICK · LOCKED' : (g.networks[0] ?? '').toUpperCase()}
        </span>
      </div>

      {/* 55% on a locked game nobody picked: still readable, but it is over and
          it was never theirs. */}
      <div className={['flex gap-[6px]', noPick ? 'opacity-55' : ''].join(' ')}>
        {cell('away')}
        {cell('home')}
      </div>

      {/* The track is removed at final. By then the spread has no job - the
          scores above it are the answer it was estimating. */}
      {!done && <SpreadTrack game={g} />}
    </li>
  )
}

export function TabBar({
  tab,
  setTab,
  isAdmin,
}: {
  tab: string
  setTab: (t: string) => void
  isAdmin: boolean
}) {
  const tabs = ['Picks', 'This Week', 'Season', ...(isAdmin ? ['Setup'] : [])]
  return (
    <nav className="sticky bottom-0 flex border-t border-hairline bg-card">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setTab(t)}
          className={[
            'slate-tap flex-1 px-2 py-3 text-sm font-bold tracking-[0.06em] uppercase',
            tab === t ? 'text-ink shadow-[inset_0_2px_0_var(--color-gold)]' : 'text-mute',
          ].join(' ')}
        >
          {t}
        </button>
      ))}
    </nav>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-10 text-center text-sm text-mute">{children}</p>
}

/**
 * WK 1 · THU SEP 3 – MON SEP 7
 *
 * The same line on every screen that shows a week, because "which week is
 * this, and which days does it cover" was answerable only on Admin - and the
 * dates are what tell you whether a game you are thinking of is even eligible.
 *
 * One component rather than a line per header: the two places that already
 * wrote this sentence had drifted into describing the same window differently,
 * and four copies would drift further.
 *
 * Renders nothing without a week. A season between boards has no dates, and an
 * empty line where a date belongs reads as a failure to load.
 */
export function WeekDates({
  week,
  tz,
}: {
  week: { number: number; opensAt: number; closesAt: number } | null
  tz?: string
}) {
  if (!week) return null
  return (
    <span className="slate-num block text-[9px] tracking-[0.08em] text-faint uppercase">
      WK {week.number} &middot; {windowLabel(week.opensAt, week.closesAt, tz)}
    </span>
  )
}
